import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { adminListProducts, adminDeleteProduct, adminUpdateProduct, adminMe, imgUrl } from "@/lib/api";
import { toast } from "sonner";
import AdminAnalytics from "./admin/AdminAnalytics";

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "girls", label: "Girls" },
  { key: "boys", label: "Boys" },
  { key: "women", label: "Women" },
  { key: "men", label: "Teens" },
  { key: "adults", label: "Adults" },
  { key: "twinning", label: "Twinning" },
];

const AdminDashboard = () => {
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [email, setEmail] = useState(localStorage.getItem("melavi_admin_email") || "");
  const [tab, setTab] = useState(
    (typeof window !== "undefined" && localStorage.getItem("melavi_admin_tab")) || "products"
  );

  useEffect(() => {
    if (typeof window !== "undefined") localStorage.setItem("melavi_admin_tab", tab);
  }, [tab]);

  const load = async () => {
    setLoading(true);
    try {
      const [p, me] = await Promise.all([adminListProducts(), adminMe()]);
      setProducts(p);
      setEmail(me.email);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("melavi_admin_token");
        nav("/admin/login");
      } else {
        toast.error("Failed to load products");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("melavi_admin_token")) {
      nav("/admin/login");
      return;
    }
    load();
  }, []);

  const logout = () => {
    localStorage.removeItem("melavi_admin_token");
    localStorage.removeItem("melavi_admin_email");
    nav("/admin/login");
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete "${slug}"? This cannot be undone.`)) return;
    try {
      await adminDeleteProduct(slug);
      setProducts((prev) => prev.filter((p) => p.slug !== slug));
      toast.success(`${slug} deleted`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const saveStock = async (slug, size, value) => {
    const v = Math.max(0, parseInt(value, 10) || 0);
    const prod = products.find((p) => p.slug === slug);
    if (!prod) return;
    const nextStock = { ...(prod.stock || {}), [size]: v };
    // optimistic update
    setProducts((prev) => prev.map((p) => (p.slug === slug ? { ...p, stock: nextStock } : p)));
    try {
      await adminUpdateProduct(slug, { stock: nextStock });
    } catch (err) {
      toast.error(`Could not save stock for ${slug}`);
      // revert on failure
      setProducts((prev) => prev.map((p) => (p.slug === slug ? prod : p)));
    }
  };

  const filtered = useMemo(() => {
    return products.filter((p) => {
      if (filter === "adults" && !["women", "men"].includes(p.category)) return false;
      if (filter === "twinning" && !p.pair_slug) return false;
      if (!["all", "adults", "twinning"].includes(filter) && p.category !== filter) return false;
      if (q && !`${p.name} ${p.slug} ${p.motif}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [products, filter, q]);

  const [dlBusy, setDlBusy] = useState(null); // 'pdf-gen' | 'zip-gen' | null
  const [catStatus, setCatStatus] = useState({ pdf: { status: "idle" }, zip: { status: "idle" } });

  const refreshStatus = React.useCallback(async () => {
    try {
      const token = localStorage.getItem("melavi_admin_token");
      const base = process.env.REACT_APP_BACKEND_URL;
      const r = await fetch(`${base}/api/admin/catalogue/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setCatStatus(await r.json());
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    refreshStatus();
    const pdfGenerating = catStatus.pdf?.status === "generating";
    const zipGenerating = catStatus.zip?.status === "generating";
    if (pdfGenerating || zipGenerating) {
      const t = setInterval(refreshStatus, 3000);
      return () => clearInterval(t);
    }
  }, [refreshStatus, catStatus.pdf?.status, catStatus.zip?.status]);

  const generateCat = async (kind) => {
    setDlBusy(`${kind}-gen`);
    const token = localStorage.getItem("melavi_admin_token");
    const base = process.env.REACT_APP_BACKEND_URL;
    try {
      const r = await fetch(`${base}/api/admin/catalogue/generate/${kind}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success(`Building ${kind === "pdf" ? "catalogue PDF" : "WhatsApp cards"} in the background — the Download button will light up when ready (~30-60s).`);
      await refreshStatus();
    } catch (err) {
      toast.error(`Generate failed: ${err.message}`);
    } finally {
      setDlBusy(null);
    }
  };

  const downloadCat = async (kind) => {
    const token = localStorage.getItem("melavi_admin_token");
    const base = process.env.REACT_APP_BACKEND_URL;
    const url = `${base}/api/admin/catalogue/download/${kind}`;
    const filename = kind === "pdf" ? "melavi-catalogue.pdf" : "melavi-whatsapp-cards.zip";
    try {
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) {
        if (resp.status === 425) throw new Error("Not generated yet — click Generate first.");
        throw new Error(`HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success(kind === "pdf" ? "Catalogue downloaded" : "WhatsApp cards downloaded");
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-ground pb-24" data-testid="admin-dashboard-page">
      {/* Admin sub-header */}
      <div className="hairline-b bg-ground/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div>
              <div className="nav-item" style={{ color: "var(--stone)" }}>MELAVI · Admin</div>
              <div className="serif text-2xl mt-1">{tab === "analytics" ? "Analytics" : "Products"}</div>
            </div>
            <div className="hairline flex" data-testid="admin-tabs">
              <button
                onClick={() => setTab("products")}
                data-testid="admin-tab-products"
                className={`nav-item px-3 py-1.5 transition-colors ${
                  tab === "products" ? "bg-ink text-ground" : "hover:bg-ink/5"
                }`}
              >
                Products
              </button>
              <button
                onClick={() => setTab("analytics")}
                data-testid="admin-tab-analytics"
                className={`nav-item px-3 py-1.5 border-l transition-colors ${
                  tab === "analytics" ? "bg-ink text-ground" : "hover:bg-ink/5"
                }`}
                style={{ borderLeftColor: "var(--ink)" }}
              >
                Analytics
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap justify-end">
            <span className="nav-item hidden md:inline" style={{ color: "var(--stone)" }}>
              {email}
            </span>

            {tab === "products" && (
              <>
            {/* Catalogue PDF — generate then download */}
            <div className="hairline flex items-stretch" data-testid="cat-pdf-group">
              <button
                onClick={() => generateCat("pdf")}
                disabled={dlBusy !== null || catStatus.pdf?.status === "generating"}
                className="nav-item px-3 py-1.5 hover:bg-ink hover:text-ground transition-colors disabled:opacity-50"
                data-testid="admin-generate-pdf"
                title="Build the catalogue PDF in the background (~30-60s)"
              >
                {catStatus.pdf?.status === "generating" ? "Generating…" : "① Generate PDF"}
              </button>
              <button
                onClick={() => downloadCat("pdf")}
                disabled={catStatus.pdf?.status !== "ready"}
                className={`nav-item px-3 py-1.5 border-l transition-colors ${
                  catStatus.pdf?.status === "ready"
                    ? "bg-ink text-ground"
                    : "opacity-40 cursor-not-allowed"
                }`}
                style={{ borderLeftColor: "var(--ink)" }}
                data-testid="admin-download-pdf"
                title={
                  catStatus.pdf?.status === "ready"
                    ? `Download the catalogue PDF (${((catStatus.pdf.size || 0) / 1024 / 1024).toFixed(1)} MB)`
                    : "Click Generate first"
                }
              >
                ② Download
              </button>
            </div>

            {/* WhatsApp cards — generate then download */}
            <div className="hairline flex items-stretch" data-testid="cat-zip-group">
              <button
                onClick={() => generateCat("zip")}
                disabled={dlBusy !== null || catStatus.zip?.status === "generating"}
                className="nav-item px-3 py-1.5 hover:bg-ink hover:text-ground transition-colors disabled:opacity-50"
                data-testid="admin-generate-zip"
                title="Build all WhatsApp product cards in the background (~30-60s)"
              >
                {catStatus.zip?.status === "generating" ? "Generating…" : "① Generate cards"}
              </button>
              <button
                onClick={() => downloadCat("zip")}
                disabled={catStatus.zip?.status !== "ready"}
                className={`nav-item px-3 py-1.5 border-l transition-colors ${
                  catStatus.zip?.status === "ready"
                    ? "bg-ink text-ground"
                    : "opacity-40 cursor-not-allowed"
                }`}
                style={{ borderLeftColor: "var(--ink)" }}
                data-testid="admin-download-zip"
                title={
                  catStatus.zip?.status === "ready"
                    ? `Download all WhatsApp cards (${((catStatus.zip.size || 0) / 1024 / 1024).toFixed(1)} MB)`
                    : "Click Generate first"
                }
              >
                ② Download
              </button>
            </div>

            <Link to="/admin/product/new" className="btn-solid" data-testid="admin-new-product">
              + New product
            </Link>
              </>
            )}
            <button onClick={logout} className="nav-item" data-testid="admin-logout">
              Log out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 pt-8">
        {tab === "analytics" ? (
          <AdminAnalytics />
        ) : (
          <>
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-8" data-testid="admin-filters">
          <div className="flex gap-2 flex-wrap">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                onClick={() => setFilter(c.key)}
                data-testid={`admin-filter-${c.key}`}
                className={`nav-item px-3 py-1 hairline transition-colors ${
                  filter === c.key ? "bg-ink text-ground" : "hover:border-ink"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, slug, motif…"
            className="ml-auto w-full md:w-72 px-3 py-1.5 hairline bg-transparent focus:outline-none focus:border-ink"
            data-testid="admin-search"
          />
          <span className="nav-item" style={{ color: "var(--stone)" }}>
            {filtered.length} / {products.length}
          </span>
        </div>

        {/* Table */}
        {loading ? (
          <div className="py-24 text-center nav-item" style={{ color: "var(--stone)" }}>
            Loading…
          </div>
        ) : (
          <div className="hairline">
            <table className="w-full text-sm" data-testid="admin-products-table">
              <thead>
                <tr className="hairline-b" style={{ background: "rgba(28,26,25,0.04)" }}>
                  <th className="text-left px-4 py-3 nav-item" style={{ color: "var(--stone)" }}>Image</th>
                  <th className="text-left px-4 py-3 nav-item" style={{ color: "var(--stone)" }}>Name</th>
                  <th className="text-left px-4 py-3 nav-item" style={{ color: "var(--stone)" }}>Category</th>
                  <th className="text-left px-4 py-3 nav-item" style={{ color: "var(--stone)" }}>Motif</th>
                  <th className="text-right px-4 py-3 nav-item" style={{ color: "var(--stone)" }}>Price</th>
                  <th className="text-left px-4 py-3 nav-item" style={{ color: "var(--stone)" }}>Stock · by size</th>
                  <th className="text-center px-4 py-3 nav-item" style={{ color: "var(--stone)" }}>Published</th>
                  <th className="text-right px-4 py-3 nav-item" style={{ color: "var(--stone)" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const hero = (p.images || []).find((i) => i.role === "hero") || (p.images || [])[0];
                  const sizes = (p.sizes && p.sizes.length) ? p.sizes : ["one-size"];
                  const total = sizes.reduce((a, s) => a + ((p.stock || {})[s] || 0), 0);
                  return (
                    <tr key={p.slug} className="hairline-b hover:bg-black/[0.02] align-top" data-testid={`admin-row-${p.slug}`}>
                      <td className="px-4 py-3">
                        <div className="w-16 h-16 bg-sand overflow-hidden">
                          {hero?.url && (
                            <img src={imgUrl(hero.url)} alt="" className="w-full h-full object-cover" />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="serif text-base">{p.name}</div>
                        <div className="text-xs" style={{ color: "var(--stone)" }}>{p.slug}</div>
                      </td>
                      <td className="px-4 py-3 capitalize">{p.category}</td>
                      <td className="px-4 py-3 capitalize">{p.motif}</td>
                      <td className="px-4 py-3 text-right">₹{p.price?.toLocaleString?.() || p.price}</td>
                      <td className="px-4 py-3" data-testid={`admin-stock-cell-${p.slug}`}>
                        <div className="flex flex-col gap-1.5">
                          {sizes.map((s) => {
                            const v = (p.stock || {})[s] || 0;
                            return (
                              <label key={s} className="flex items-center gap-2 text-xs">
                                <span className="w-14 uppercase tracking-[0.18em]" style={{ color: "var(--stone)" }}>{s}</span>
                                <input
                                  type="number"
                                  min="0"
                                  defaultValue={v}
                                  onBlur={(e) => {
                                    if (parseInt(e.target.value, 10) !== v) saveStock(p.slug, s, e.target.value);
                                  }}
                                  className={`w-14 px-2 py-1 hairline bg-transparent text-center focus:outline-none focus:border-ink ${v === 0 ? "text-[color:var(--accent)]" : ""}`}
                                  data-testid={`admin-row-stock-${p.slug}-${s}`}
                                />
                              </label>
                            );
                          })}
                          <div className="text-[10px] pt-1 tracking-widest uppercase" style={{ color: "var(--stone)" }}>
                            Total: <strong style={{ color: total === 0 ? "var(--accent)" : "var(--ink)" }}>{total}</strong>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`nav-item px-2 py-0.5 hairline ${p.published ? "" : "opacity-50"}`}
                          data-testid={`admin-published-${p.slug}`}
                        >
                          {p.published ? "Live" : "Draft"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex gap-3 justify-end">
                          <Link
                            to={`/admin/product/${p.slug}`}
                            className="crimson-link nav-item"
                            data-testid={`admin-edit-${p.slug}`}
                          >
                            Edit
                          </Link>
                          <button
                            onClick={() => remove(p.slug)}
                            className="nav-item text-ink/50 hover:text-ink"
                            data-testid={`admin-delete-${p.slug}`}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="py-16 text-center nav-item" style={{ color: "var(--stone)" }}>
                No products match your filters.
              </div>
            )}
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
